import { splitNumberWithBadge } from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

export type ColumnType = `number` | `currency` | `text` | `percentage`;

export type ColumnTypes = Record<string, string>;

const KNOWN_COLUMN_TYPES: ColumnType[] = [
  `number`,
  `currency`,
  `text`,
  `percentage`,
];

function isKnownColumnType(value: string | undefined): value is ColumnType {
  return !!value && (KNOWN_COLUMN_TYPES as string[]).includes(value);
}

/**
 * Parses the `column_types` map from a `default_view` block. Keeps only
 * string values; unknown/future type names are preserved so they can still
 * be looked up (and then rendered as raw text, per spec) rather than
 * dropped.
 */
export function normalizeColumnTypes(raw: unknown): ColumnTypes | null {
  if (typeof raw !== `object` || raw === null) {
    return null;
  }

  const columnTypes = Object.entries(
    raw as Record<string, unknown>,
  ).reduce<ColumnTypes>((normalized, [column, type]) => {
    if (column.trim() && typeof type === `string` && type.trim()) {
      normalized[column.trim()] = type.trim().toLowerCase();
    }
    return normalized;
  }, {});

  return Object.keys(columnTypes).length > 0 ? columnTypes : null;
}

/**
 * Resolves a column's type from `column_types`, falling back to a
 * case-insensitive header match. Returns null when there is no entry, or
 * the entry is an unrecognized type, so callers can render the column as
 * plain text instead of hard-failing on new/unknown type names.
 */
export function resolveColumnType(
  columnTypes: ColumnTypes | null | undefined,
  header: string,
): ColumnType | null {
  if (!columnTypes || !header) {
    return null;
  }

  const directMatch = columnTypes[header];
  const normalizedHeader = header.trim().toLowerCase();
  const fallbackMatch = Object.entries(columnTypes).find(
    ([column]) => column.trim().toLowerCase() === normalizedHeader,
  )?.[1];
  const resolvedType = directMatch ?? fallbackMatch;

  return isKnownColumnType(resolvedType) ? resolvedType : null;
}

const NUMERIC_VALUE_PATTERN = /^(\()?([+-])?(\d+(?:,\d{3})*)(\.\d+)?(\))?$/;

function formatNumberWithColumnTypeAffixes(
  numberPart: string,
  columnType: ColumnType,
  currencySymbol: string,
): string | null {
  const match = numberPart.trim().match(NUMERIC_VALUE_PATTERN);

  if (!match) {
    return null;
  }

  const [
    ,
    openParen = ``,
    sign = ``,
    integerPart,
    decimalPart = ``,
    closeParen = ``,
  ] = match;
  const integerWithCommas = Number(
    integerPart.replace(/,/g, ``),
  ).toLocaleString(`en-US`);
  const core = `${integerWithCommas}${decimalPart}`;

  if (columnType === `currency`) {
    return `${openParen}${sign}${currencySymbol}${core}${closeParen}`;
  }

  if (columnType === `percentage`) {
    return `${openParen}${sign}${core}%${closeParen}`;
  }

  return `${openParen}${sign}${core}${closeParen}`;
}

export interface FormattedTableCell {
  badge?: string;
  text: string;
}

/**
 * Formats a raw (unformatted, no `$`/`%`) cell value according to its
 * column's type. Values that aren't plain numbers (e.g. "N/A", free text)
 * are returned unchanged, as are `text` columns and columns with no
 * resolved type.
 */
export function formatTableCellByColumnType(
  rawValue: string,
  columnType: ColumnType | null,
  currencySymbol: string,
): FormattedTableCell {
  const trimmedValue = rawValue.trim();

  if (!columnType || columnType === `text` || !trimmedValue) {
    return { text: rawValue };
  }

  const withBadge = splitNumberWithBadge(trimmedValue);
  const numberPart = withBadge ? withBadge.number : trimmedValue;
  const formattedNumber = formatNumberWithColumnTypeAffixes(
    numberPart,
    columnType,
    currencySymbol,
  );

  if (formattedNumber === null) {
    return { text: rawValue };
  }

  return withBadge
    ? { badge: withBadge.badge, text: formattedNumber }
    : { text: formattedNumber };
}

/**
 * Compact chart-value formatter driven by column type, for use when the
 * `default_view` hint carries `column_types`. Mirrors
 * `formatTableCellByColumnType` but on an already-compacted numeric string
 * (e.g. `1.2K`).
 */
export function formatCompactValueByColumnType(
  compactValue: string,
  columnType: ColumnType | null,
  currencySymbol: string,
): string {
  if (columnType === `currency`) {
    return `${currencySymbol}${compactValue}`;
  }

  if (columnType === `percentage`) {
    return `${compactValue}%`;
  }

  return compactValue;
}
